import React, { useCallback, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Printer } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import MenuBar from "../../components/MenuBar";
import HeaderBar from "../../components/HeaderBar";
import { ATTENDANCE_BRANCHES } from "../../config/attendanceBranches";
import { getVisibleAttendanceBranches } from "../../config/appBranches";
import { loadMusterCardsForMonth, fmtHM, fmtClockShort } from "../../helpers/payrollMusterCard";

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

const STATUS_STYLES = {
  P: "text-emerald-700",
  "½P": "text-amber-600",
  A: "text-rose-600 font-bold",
  WO: "text-slate-400",
  H: "text-indigo-600",
  L: "text-sky-600",
  "": "text-slate-300",
};

function SummaryStat({ label, value }) {
  return (
    <span className="whitespace-nowrap">
      <span className="text-slate-500">{label}:</span>{" "}
      <span className="font-bold tabular-nums text-slate-900">{value}</span>
    </span>
  );
}

/** One metric row across all day columns. */
function MetricRow({ label, days, render }) {
  return (
    <tr className="border-t border-slate-100">
      <th className="whitespace-nowrap bg-slate-50 px-1 py-1 text-left text-[10px] font-bold uppercase tracking-wide text-slate-600">
        {label}
      </th>
      {days.map((d) => (
        <td
          key={d.dayKey}
          className={`whitespace-nowrap px-0.5 py-1 text-center text-[10px] leading-tight tabular-nums ${
            d.status === "WO" || d.status === "H" ? "bg-slate-50/70" : ""
          }`}
        >
          {render(d)}
        </td>
      ))}
    </tr>
  );
}

/** Full muster card for one employee (summary header + per-day grid). */
function EmployeeCard({ emp, shopLabel, monthLabel, pageBreakAfter }) {
  return (
    <div
      className={`muster-print-card mt-5 break-inside-avoid rounded-xl border border-slate-200 bg-white p-4 shadow-sm print:border-0 print:shadow-none ${
        pageBreakAfter ? "print:break-after-page" : ""
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 pb-3">
        <div>
          <div className="emp-name text-lg font-bold text-slate-900">
            {emp.employeeId} : {emp.employeeName}
          </div>
          <div className="emp-sub text-xs text-slate-500">
            {[emp.designation, emp.department].filter(Boolean).join(" · ") || shopLabel} · {monthLabel}
          </div>
        </div>
      </div>

      <div className="emp-summary mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs">
        <SummaryStat label="Total Work Duration" value={fmtHM(emp.summary.workMs)} />
        <SummaryStat label="Total OT" value={fmtHM(emp.summary.otMs)} />
        <SummaryStat label="Total Duration (+OT)" value={fmtHM(emp.summary.totalDurationWithOtMs)} />
        <SummaryStat label="Avg Working Hrs" value={fmtHM(emp.summary.avgWorkMs)} />
        <SummaryStat label="Present" value={emp.summary.presentUnits} />
        <SummaryStat label="Absent" value={emp.summary.absentDays} />
        <SummaryStat label="Weekly Off" value={emp.summary.weeklyOff} />
        <SummaryStat label="Holidays" value={emp.summary.holidays} />
        <SummaryStat label="Leaves Taken" value={emp.summary.leaves} />
        <SummaryStat label="Late By Hrs" value={fmtHM(emp.summary.lateMs)} />
        <SummaryStat label="Late By Days" value={emp.summary.lateDays} />
        <SummaryStat label="Early By Hrs" value={fmtHM(emp.summary.earlyMs)} />
        <SummaryStat label="Early going Days" value={emp.summary.earlyDays} />
      </div>

      <div className="mt-4 rounded-lg border border-slate-200">
        <table className="w-full table-fixed border-collapse text-left">
          <colgroup>
            <col className="w-[60px]" />
            {emp.days.map((d) => (
              <col key={d.dayKey} />
            ))}
          </colgroup>
          <thead>
            <tr className="bg-slate-800 text-slate-100">
              <th className="bg-slate-800 px-1 py-1.5 text-[10px] font-bold uppercase tracking-wide">Date</th>
              {emp.days.map((d) => (
                <td
                  key={d.dayKey}
                  className={`px-0.5 py-1 text-center text-[10px] font-semibold leading-tight ${
                    d.status === "WO" ? "text-amber-300" : ""
                  }`}
                >
                  <div className="tabular-nums">{d.dayNum}</div>
                  <div className="text-[10px] font-normal text-slate-300">{d.weekday}</div>
                </td>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-slate-200 bg-white">
              <th className="bg-slate-50 px-1 py-1 text-left text-[10px] font-bold uppercase tracking-wide text-slate-600">
                Status
              </th>
              {emp.days.map((d) => (
                <td
                  key={d.dayKey}
                  className={`px-0.5 py-1 text-center text-[10px] font-bold ${STATUS_STYLES[d.status] || ""}`}
                >
                  {d.status || "·"}
                </td>
              ))}
            </tr>
            <MetricRow label="InTime 1" days={emp.days} render={(d) => fmtClockShort(d.in1) || "—"} />
            <MetricRow label="OutTime 1" days={emp.days} render={(d) => fmtClockShort(d.out1) || "—"} />
            <MetricRow label="InTime 2" days={emp.days} render={(d) => fmtClockShort(d.in2) || "—"} />
            <MetricRow label="OutTime 2" days={emp.days} render={(d) => fmtClockShort(d.out2) || "—"} />
            <MetricRow label="Duration" days={emp.days} render={(d) => (d.durationMs > 0 ? fmtHM(d.durationMs) : "—")} />
            <MetricRow label="Late By" days={emp.days} render={(d) => (d.lateMs > 60000 ? fmtHM(d.lateMs) : "—")} />
            <MetricRow label="Early By" days={emp.days} render={(d) => (d.earlyMs > 60000 ? fmtHM(d.earlyMs) : "—")} />
            <MetricRow label="OT" days={emp.days} render={(d) => (d.otMs > 60000 ? fmtHM(d.otMs) : "—")} />
          </tbody>
        </table>
      </div>

      <p className="muster-legend mt-3 text-[10px] text-slate-500">
        Legend: <span className="font-semibold text-emerald-700">P</span> present ·{" "}
        <span className="font-semibold text-amber-600">½P</span> half day ·{" "}
        <span className="font-semibold text-rose-600">A</span> absent ·{" "}
        <span className="font-semibold text-slate-400">WO</span> weekly off ·{" "}
        <span className="font-semibold text-indigo-600">H</span> holiday ·{" "}
        <span className="font-semibold text-sky-600">L</span> paid leave.
      </p>
    </div>
  );
}

const ALL_STAFF = "__ALL__";

/** Landscape print, 3 employee cards per page, compact sizing. */
const PRINT_STYLE = `
@media print {
  @page { size: A4 landscape; margin: 6mm; }
  .muster-print-card { margin-top: 6px !important; padding: 6px !important; }
  .muster-print-card .emp-name { font-size: 11px !important; }
  .muster-print-card .emp-sub { font-size: 8px !important; }
  .muster-print-card .emp-summary { font-size: 8px !important; }
  .muster-print-card td,
  .muster-print-card th { font-size: 7px !important; padding-top: 1px !important; padding-bottom: 1px !important; }
  .muster-print-card .muster-legend { display: none !important; }
}
`;

export default function PayrollEmployeeAttendanceCard() {
  const storedUser = useMemo(() => getStoredUser(), []);
  const visibleBranches = useMemo(() => getVisibleAttendanceBranches(storedUser), [storedUser]);
  const branchPool = visibleBranches.length ? visibleBranches : ATTENDANCE_BRANCHES;

  const [month, setMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [shopLabel, setShopLabel] = useState(branchPool[0]?.label || ATTENDANCE_BRANCHES[0].label);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selectedEmpId, setSelectedEmpId] = useState("");

  const currentShop = useMemo(
    () => branchPool.find((b) => b.label === shopLabel) || branchPool[0],
    [branchPool, shopLabel]
  );

  const shiftMonth = (delta) => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));

  const load = useCallback(async () => {
    if (!currentShop) return;
    setLoading(true);
    try {
      const res = await loadMusterCardsForMonth(currentShop, month);
      setData(res);
      setSelectedEmpId((prev) => {
        if (prev === ALL_STAFF) return ALL_STAFF;
        if (prev && res.employees.some((e) => e.employeeId === prev)) return prev;
        return res.employees[0]?.employeeId || "";
      });
      if (res.employees.length === 0) toast("No working employees found for this branch.");
      else toast.success(`Loaded ${res.employees.length} employees`);
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [currentShop, month]);

  const showAll = selectedEmpId === ALL_STAFF;
  const emp = useMemo(
    () => (showAll ? null : data?.employees.find((e) => e.employeeId === selectedEmpId) || null),
    [data, selectedEmpId, showAll]
  );
  const visibleEmployees = useMemo(
    () => (showAll ? data?.employees || [] : emp ? [emp] : []),
    [showAll, data, emp]
  );

  const monthLabel = month.toLocaleString("en-IN", { month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-slate-100/90 print:bg-white">
      <style>{PRINT_STYLE}</style>
      <Toaster position="top-right" />
      <div className="print:hidden">
        <MenuBar />
        <HeaderBar />
      </div>
      <main className="w-full px-4 py-6 print:px-0 print:py-0">
        <div className="print:hidden">
          <h1 className="text-2xl font-bold text-slate-900">Monthly Status Report (Detailed Work Duration(Four Punch))</h1>
        </div>

        <div className="mt-3 flex flex-wrap items-end gap-3 print:hidden">
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-2 py-1 shadow-sm">
            <button type="button" onClick={() => shiftMonth(-1)} className="rounded-lg p-1 hover:bg-slate-100">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="min-w-[10rem] text-center text-sm font-semibold">{monthLabel}</span>
            <button type="button" onClick={() => shiftMonth(1)} className="rounded-lg p-1 hover:bg-slate-100">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          <label className="text-xs font-semibold text-slate-700">
            Branch
            <select
              value={shopLabel}
              onChange={(e) => setShopLabel(e.target.value)}
              className="mt-1 block min-w-[10rem] rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
            >
              {branchPool.map((b) => (
                <option key={b.label} value={b.label}>
                  {b.label}
                </option>
              ))}
            </select>
          </label>
          {data && data.employees.length > 0 && (
            <label className="text-xs font-semibold text-slate-700">
              Employee
              <select
                value={selectedEmpId}
                onChange={(e) => setSelectedEmpId(e.target.value)}
                className="mt-1 block min-w-[16rem] rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
              >
                <option value={ALL_STAFF}>All staff ({data.employees.length})</option>
                {data.employees.map((e) => (
                  <option key={e.employeeId} value={e.employeeId}>
                    {e.employeeName} ({e.employeeId})
                  </option>
                ))}
              </select>
            </label>
          )}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white shadow hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? "Loading…" : "Load"}
          </button>
          {data && visibleEmployees.length > 0 && (
            <button
              type="button"
              onClick={() => window.print()}
              className="ml-auto inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
            >
              <Printer className="h-4 w-4" /> Print
            </button>
          )}
        </div>

        {!data && (
          <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-white/60 p-8 text-center text-sm text-slate-500 print:hidden">
            Pick a branch and month, then click Load.
          </div>
        )}

        {data && data.employees.length > 0 && visibleEmployees.length === 0 && (
          <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-white/60 p-8 text-center text-sm text-slate-500 print:hidden">
            Select an employee.
          </div>
        )}

        {visibleEmployees.map((e, idx) => (
          <EmployeeCard
            key={e.employeeId}
            emp={e}
            shopLabel={shopLabel}
            monthLabel={monthLabel}
            pageBreakAfter={idx % 3 === 2}
          />
        ))}
      </main>
    </div>
  );
}
